import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, InlineNotice, Label, RadioGroup, SearchableSelect, TagInput, useDebouncedValue, } from '@wit-support/ui';
import { CountryPicker, formatCountryListWithin } from '@wit-support/ui-domain';
import { DateTimeWidget, FieldShell, MultiSelectWidget, } from '@wit-support/ui-forms';
import { listUsedInterests, searchInterests } from '../api.js';
import { ADS_QUERY_DEFAULTS, SEARCH_STALE_MS, USED_INTERESTS_STALE_MS } from '../query-policy.js';
import { META_SEARCH_MIN_CHARS, buildInterestSuggestions, inAdSetsLabel, } from './interest-suggestions.js';
import { ADS_TZ, ADS_TZ_INPUT_LABEL } from '../format.js';
import { NotConfigurableWidget, STRUCTURED_WIDGETS } from './structured-widgets.js';
const EMPTY = {
    registry: null,
    locales: null,
    countryLists: null,
    platformAccountId: null,
    audienceSlotNames: null,
    accountStartHours: null,
    currency: null,
    immediateLeadMinutes: null,
};
const RegistryDataContext = createContext(EMPTY);
export function RegistryDataProvider({ value, children, }) {
    const merged = useMemo(() => ({ ...EMPTY, ...value }), [value]);
    return _jsx(RegistryDataContext.Provider, { value: merged, children: children });
}
export function useRegistryData() {
    return useContext(RegistryDataContext);
}
function shellProps(p) {
    return {
        meta: p.meta,
        fieldState: p.fieldState,
        label: p.label,
        description: p.description,
        hint: p.hint,
        htmlFor: p.field.name,
    };
}
// --- возраст и прочие «от–до» ----------------------------------------------
export function IntRangeWidget(p) {
    const v = (p.field.value ?? {});
    const set = (k, raw) => {
        const n = raw === '' ? undefined : Number(raw);
        p.field.onChange({ ...v, [k]: n });
    };
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Input, { id: p.field.name, type: "number", "aria-label": "\u041E\u0442", className: "w-24", value: v.min ?? '', disabled: p.disabled, onBlur: p.field.onBlur, onChange: (e) => set('min', e.target.value) }), _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2013" }), _jsx(Input, { type: "number", "aria-label": "\u0414\u043E", className: "w-24", value: v.max ?? '', disabled: p.disabled, onBlur: p.field.onBlur, onChange: (e) => set('max', e.target.value) })] }) }));
}
export function GeoWidget(p) {
    const v = (p.field.value ?? { mode: 'WORLDWIDE' });
    const options = p.meta.adsOptions ?? [
        { value: 'WORLDWIDE', label: 'Весь мир' },
        { value: 'COUNTRIES', label: 'Страны' },
    ];
    const countries = v.mode === 'COUNTRIES' ? v.countries : [];
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(RadioGroup, { name: p.field.name, value: v.mode, disabled: p.disabled, onValueChange: (mode) => p.field.onChange(mode === 'WORLDWIDE' ? { mode } : { mode, countries }), className: "flex flex-wrap gap-4", children: options.map((o) => {
                        const id = `${p.field.name}__${o.value}`;
                        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RadioGroup.Item, { value: o.value, id: id }), _jsx(Label, { htmlFor: id, children: o.label })] }, o.value));
                    }) }), v.mode === 'COUNTRIES' ? (
                // хранится как раньше — ISO-коды; ввод — поиск по названию или коду,
                // вставка «DE, FR, IT» по-прежнему работает
                _jsx(CountryPicker, { "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0441\u0442\u0440\u0430\u043D\u044B", value: countries, disabled: p.disabled === true, onChange: (next) => p.field.onChange({ mode: 'COUNTRIES', countries: next }) })) : null] }) }));
}
/**
 * Список ISO-кодов стран (`country_codes` реестра, страны списка исключений):
 * поиск по названию и коду вместо ручного ввода кодов. Значение — массив
 * кодов, как и у кита `TagsWidget`, который этот виджет заменяет.
 */
export function CountryCodesWidget(p) {
    const codes = (Array.isArray(p.field.value) ? p.field.value : []);
    const max = typeof p.meta.maxCountries === 'number' ? p.meta.maxCountries : undefined;
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsx(CountryPicker, { "aria-label": typeof p.label === 'string' ? p.label : 'Страны', value: codes.map((c) => String(c).trim().toUpperCase()), disabled: p.disabled === true || p.readonly === true, onChange: (next) => p.field.onChange(next), ...(max != null ? { maxCountries: max } : {}), ...(typeof p.meta.placeholder === 'string' ? { placeholder: p.meta.placeholder } : {}) }) }));
}
// --- языки -------------------------------------------------------------------
export function LocalesWidget(p) {
    const { registry, locales } = useRegistryData();
    const ids = (Array.isArray(p.field.value) ? p.field.value : []);
    // пресеты и запрещённые id — из реестра; справочник сервиса — только названия
    const presets = registry?.localePresets ?? [];
    const banned = new Set(registry?.bannedLocaleIds ?? []);
    const items = locales?.items ?? [];
    const options = items
        .filter((l) => !banned.has(l.localeId))
        .map((l) => ({ value: String(l.localeId), label: l.name }));
    // Выбранные id без названия в справочнике — тоже опции, иначе чип станет «#6».
    for (const id of ids) {
        if (!options.some((o) => o.value === String(id))) {
            options.push({
                value: String(id),
                label: items.find((l) => l.localeId === id)?.name ?? `#${id}`,
            });
        }
    }
    const hasBanned = ids.some((id) => banned.has(id));
    const asStrings = {
        ...p,
        field: {
            ...p.field,
            value: ids.map(String),
            onChange: (next) => p.field.onChange(next.map(Number)),
        },
        // Поиск по названию и id; выбранные — чипы прямо в строке ввода.
        meta: { ...p.meta, options, placeholder: 'Найти язык…' },
    };
    return (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(MultiSelectWidget, { ...asStrings }), presets.length > 0 ? (_jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041F\u0440\u0435\u0441\u0435\u0442\u044B:" }), presets.map((pr) => (_jsx(Button, { type: "button", size: "sm", variant: "secondary", disabled: p.disabled, onClick: () => p.field.onChange([...pr.localeIds]), children: pr.label }, pr.key)))] })) : null, hasBanned ? (_jsx(InlineNotice, { variant: "danger", as: "div", children: "\u00ABEnglish (All)\u00BB \u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u0437\u0430\u043F\u0440\u0435\u0449\u0451\u043D\u043D\u044B\u0435 \u044F\u0437\u044B\u043A\u0438 \u043D\u0435 \u0434\u043E\u043F\u0443\u0441\u043A\u0430\u044E\u0442\u0441\u044F \u2014 \u0432\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043F\u0440\u0435\u0441\u0435\u0442." })) : null] }));
}
// --- слоты аудиторий ---------------------------------------------------------
/** Предел имени слота auto-ads. */
const SLOT_NAME_MAX = 60;
/**
 * Слоты аудиторий шаблона — по ИМЕНИ: «LAL 1 %», «Покупатели год». Какая
 * аудитория стоит за именем, решает профиль каждого кабинета. Подсказки — все
 * имена из профилей кабинетов; можно ввести новое и привязать его в профиле.
 */
export function AudienceSlotsWidget(p) {
    const { audienceSlotNames } = useRegistryData();
    const value = (Array.isArray(p.field.value) ? p.field.value : []);
    const [query, setQuery] = useState('');
    const q = query.trim().toLocaleLowerCase('ru-RU');
    const known = audienceSlotNames ?? [];
    const suggestions = known
        .filter((n) => q === '' || n.toLocaleLowerCase('ru-RU').includes(q))
        .map((n) => ({ id: n, name: n }));
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(TagInput, { "aria-label": typeof p.label === 'string' ? p.label : 'Слоты аудиторий', value: value, suggestions: suggestions, query: query, onQueryChange: setQuery, disabled: p.disabled === true, placeholder: "\u0418\u043C\u044F \u0441\u043B\u043E\u0442\u0430\u2026", maxLength: SLOT_NAME_MAX, maxTags: 50, createLabel: (name) => `Новый слот «${name}»`, emptyLabel: "\u0421\u043B\u043E\u0442\u043E\u0432 \u0441 \u0442\u0430\u043A\u0438\u043C \u0438\u043C\u0435\u043D\u0435\u043C \u043D\u0435\u0442 \u2014 \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043D\u043E\u0432\u043E\u0435", onChange: (next) => p.field.onChange(next.map((n) => n.trim()).filter((n) => n !== '')) }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: known.length > 0
                        ? `Имена из профилей кабинетов: ${known.join(', ')}. В каждом кабинете запуска имя должно быть привязано к аудитории — иначе ошибка при проверке.`
                        : 'В профилях кабинетов слотов пока нет. Введите имя и привяжите его к аудитории в профиле каждого кабинета («Кабинеты» → профиль → «Слоты аудиторий»).' })] }) }));
}
/** Сколько «уже использованных» грузим разом (у нас их десятки) — фильтр дальше локальный. */
const USED_INTERESTS_FETCH = 200;
/**
 * Интересы: на пустом вводе — «Уже использовали» (интересы наших групп во всех
 * кабинетах, по числу групп); при вводе — они же, отфильтрованные локально, и
 * ниже «Ещё из Meta» (поиск по справочнику Meta от 2 символов, без повторов).
 * Выбирать можно из обеих секций; в Meta уходят только id.
 */
export function InterestsWidget(p) {
    const { platformAccountId } = useRegistryData();
    const value = (Array.isArray(p.field.value) ? p.field.value : []);
    const [query, setQuery] = useState('');
    const q = useDebouncedValue(query.trim(), 300);
    const used = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: ['ads', 'interests', 'used'],
        queryFn: () => listUsedInterests({ limit: USED_INTERESTS_FETCH }),
        staleTime: USED_INTERESTS_STALE_MS,
    });
    const metaEnabled = platformAccountId != null && q.length >= META_SEARCH_MIN_CHARS;
    const search = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: ['ads', 'interests', platformAccountId, q],
        queryFn: () => searchInterests(platformAccountId, q),
        enabled: metaEnabled,
        // поиск идёт в Meta; ответ на тот же запрос не меняется
        staleTime: SEARCH_STALE_MS,
    });
    // ответ Meta — только когда отложенный запрос догнал ввод: в паузу 300 мс выдача прошлого
    // запроса не показывается и не поднимает чужой интерес в «Уже использовали» (через совпадение id)
    const metaItems = metaEnabled && q === query.trim() ? (search.data?.items ?? []) : [];
    const suggestions = buildInterestSuggestions({
        used: used.data?.items ?? [],
        meta: metaItems,
        query,
    });
    const usageById = new Map(suggestions.map((s) => [s.id, s.usageCount]));
    const names = new Map(value.map((i) => [i.id, i.name]));
    for (const s of suggestions)
        if (!names.has(s.id))
            names.set(s.id, s.name);
    const shortQuery = query.trim().length > 0 && query.trim().length < META_SEARCH_MIN_CHARS;
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(TagInput, { identity: "id", allowCreate: false, "aria-label": typeof p.label === 'string' ? p.label : 'Интересы', value: value.map((i) => i.id), getLabel: (id) => names.get(id) ?? id, suggestions: suggestions, renderSuggestion: (s) => {
                        const n = usageById.get(s.id);
                        return (_jsxs(_Fragment, { children: [_jsx("span", { className: "truncate", children: s.name }), n != null ? (_jsx("span", { className: "shrink-0 text-xs text-[var(--color-text-muted)]", children: inAdSetsLabel(n) })) : null] }));
                    }, query: query, onQueryChange: setQuery, 
                    // «Загрузка…» заменяет весь список — только когда показать ещё нечего
                    loading: suggestions.length === 0 && (used.isFetching || search.isFetching), disabled: p.disabled || platformAccountId == null, placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0438\u043D\u0442\u0435\u0440\u0435\u0441\u2026", maxTags: 25, emptyLabel: shortQuery
                        ? 'Среди уже использованных нет — для поиска в Meta введите ещё букву'
                        : 'Ничего не найдено', onChange: (ids) => p.field.onChange(ids.map((id) => ({ id, name: names.get(id) ?? id }))) }), platformAccountId == null ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041F\u043E\u0438\u0441\u043A \u0438\u043D\u0442\u0435\u0440\u0435\u0441\u043E\u0432 \u2014 \u043F\u043E\u0441\u043B\u0435 \u0432\u044B\u0431\u043E\u0440\u0430 \u043A\u0430\u0431\u0438\u043D\u0435\u0442\u0430." })) : null] }) }));
}
// --- список исключаемых стран ------------------------------------------------
const NONE = '__none__';
/**
 * Список исключаемых стран: явно выбранный список или «Не исключать» (по
 * умолчанию). Неявного списка «по умолчанию» нет (v3 §3).
 */
export function CountryListWidget(p) {
    const { countryLists } = useRegistryData();
    const raw = p.field.value;
    const current = raw == null || raw === '' ? NONE : raw;
    return (_jsx(FieldShell, { ...shellProps(p), children: _jsx(SearchableSelect, { id: p.field.name, value: current, onChange: (v) => p.field.onChange(v === NONE ? null : v), disabled: p.disabled === true, placeholder: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0441\u043F\u0438\u0441\u043E\u043A\u2026", loading: countryLists == null, options: [
                { value: NONE, label: 'Не исключать' },
                ...(countryLists ?? []).map((l) => {
                    const { text, full } = formatCountryListWithin(l.countries);
                    return {
                        value: l.id,
                        label: `${l.name} (${l.countries.length})`,
                        description: text === '' ? 'Пустой список' : text,
                        title: full,
                    };
                }),
            ] }) }));
}
function withoutError(fs) {
    const { error: _error, ...rest } = fs;
    return { ...rest, invalid: false };
}
/**
 * Режимы старта, если реестр не прислал `options` (подписи и пояснения — как в
 * auto-ads). Пояснение «Сразу» — по запасу из данных (`nowHint`).
 */
const START_OPTIONS = [
    { value: 'now', label: 'Сразу', hint: '' },
    { value: 'at', label: 'В дату', hint: 'Дата и время по Лос-Анджелесу' },
    { value: 'midnight', label: 'По нулям', hint: 'Ближайшие 00:00 по Лос-Анджелесу' },
    {
        value: 'account_hour',
        label: 'Час кабинета',
        hint: 'Ближайший час старта из профиля кабинета (по Лос-Анджелесу)',
    },
];
const START_MODES = new Set(START_OPTIONS.map((o) => o.value));
/**
 * Запас «Сразу», мин: значение шаблона, иначе умолчание опции
 * `opt.immediateLeadMinutes` из реестра auto-ads; null — данных нет.
 */
export function immediateLeadOf(data) {
    if (data.immediateLeadMinutes != null)
        return data.immediateLeadMinutes;
    const d = data.registry?.options.find((o) => o.key === 'opt.immediateLeadMinutes')?.default;
    return typeof d === 'number' ? d : null;
}
/** Пояснение «Сразу» без реестра: число — только из данных, иначе без числа. */
export function nowHint(leadMinutes) {
    return leadMinutes != null
        ? `Старт через ${leadMinutes} мин после создания`
        : 'Старт после создания — с запасом из шаблона';
}
/** Варианты старта: из реестра (известные режимы), иначе свои. */
function startOptions(meta, leadMinutes) {
    const own = START_OPTIONS.map((o) => o.value === 'now' ? { ...o, hint: nowHint(leadMinutes) } : o);
    const fromRegistry = meta.adsOptions ?? [];
    const known = fromRegistry.filter((o) => START_MODES.has(o.value));
    if (known.length === 0)
        return own;
    return known.map((o) => ({
        value: o.value,
        label: o.label,
        hint: o.hint ?? own.find((d) => d.value === o.value)?.hint ?? '',
    }));
}
/**
 * Старт: «Сразу» (по умолчанию), «В дату», «По нулям», «Час кабинета». Всё
 * время — по Лос-Анджелесу: дата и время вводятся в нём, «По нулям» и час
 * кабинета считаются в нём (v3 §5).
 */
export function StartWidget(p) {
    const data = useRegistryData();
    const { accountStartHours } = data;
    const v = (p.field.value ?? { mode: 'now' });
    const options = startOptions(p.meta, immediateLeadOf(data));
    const current = options.find((o) => o.value === v.mode);
    // Длинная подсказка реестра заменена пояснением выбранного режима под переключателем.
    const { description: _long, ...meta } = p.meta;
    const { description: _desc, ...shell } = shellProps(p);
    return (_jsx(FieldShell, { ...shell, meta: meta, children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(RadioGroup, { name: p.field.name, value: v.mode, disabled: p.disabled, className: "flex flex-wrap gap-4", onValueChange: (mode) => p.field.onChange(mode === 'at'
                        ? { mode, at: v.mode === 'at' ? v.at : '' }
                        : { mode: mode }), children: options.map((o) => {
                        const id = `${p.field.name}__${o.value}`;
                        return (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RadioGroup.Item, { value: o.value, id: id }), _jsx(Label, { htmlFor: id, children: o.label })] }, o.value));
                    }) }), current?.hint ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "start-hint", children: current.hint })) : null, v.mode === 'at' ? (_jsx(DateTimeWidget, { field: {
                        ...p.field,
                        name: `${p.field.name}__at`,
                        value: v.at || undefined,
                        onChange: (iso) => p.field.onChange({ mode: 'at', at: typeof iso === 'string' ? iso : '' }),
                    }, 
                    // Ошибку поля показывает внешняя оболочка — внутри не дублируем.
                    fieldState: withoutError(p.fieldState), schema: p.schema, meta: { widget: 'datetime', timeZone: ADS_TZ, timeZoneLabel: ADS_TZ_INPUT_LABEL }, ...(p.disabled !== undefined ? { disabled: p.disabled } : {}) })) : null, v.mode === 'account_hour' && accountStartHours != null && accountStartHours.length > 0 ? (_jsx("ul", { className: "flex flex-col gap-0.5 text-xs", "data-testid": "account-start-hours", children: accountStartHours.map((a) => (_jsxs("li", { className: a.hour == null
                            ? 'text-[var(--color-state-danger-fg)]'
                            : 'text-[var(--color-text-secondary)]', children: [a.name, ":", ' ', a.hour == null
                                ? 'час старта не задан — заполните его в профиле кабинета'
                                : `${a.hour} (${ADS_TZ_INPUT_LABEL})`] }, a.name))) })) : null, _jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0412\u0441\u0451 \u0432\u0440\u0435\u043C\u044F \u043A\u043E\u043D\u0441\u0442\u0440\u0443\u043A\u0442\u043E\u0440\u0430 \u2014 ", ADS_TZ_INPUT_LABEL, "."] })] }) }));
}
// --- подбор по типу ------------------------------------------------------------
const BY_TYPE = {
    int_range: IntRangeWidget,
    geo: GeoWidget,
    country_codes: CountryCodesWidget,
    locales: LocalesWidget,
    audience_slots: AudienceSlotsWidget,
    interests: InterestsWidget,
    country_list: CountryListWidget,
    start: StartWidget,
    // структурные значения (квота, пол, плейсменты, …) и `json` старого реестра — без JSON
    ...STRUCTURED_WIDGETS,
};
/** Типы, для которых виджет подбирает кит по схеме (`widgetSchemaFor`). */
const KIT_TYPES = new Set([
    'enum',
    'money',
    'int',
    'decimal',
    'bool',
    'text',
    'textarea',
    'url',
    'int_list',
]);
/**
 * Виджет поля по `type` дескриптора: собственный (`BY_TYPE`; `json` старого
 * реестра — по ключу поля), `undefined` — подбирает кит, а для неизвестного
 * типа (новый тип реестра без виджета) — «Поле пока нельзя настроить здесь» и
 * значение словами: поле видно, экран не падает, JSON людям не показывается.
 */
export function widgetForType(type) {
    if (KIT_TYPES.has(type))
        return undefined;
    return BY_TYPE[type] ?? NotConfigurableWidget;
}
/** Есть ли у типа редактируемый виджет. Для тестов и диагностики. */
export function hasDedicatedWidget(type) {
    return KIT_TYPES.has(type) || type in BY_TYPE;
}
//# sourceMappingURL=field-widget.js.map